import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import {
    DAYS_PER_WEEK,
    SNAP_DELAY_MS,
    SNAP_DURATION_MS,
    easeOutCubic,
    getSnapTargetDay,
    getViewportCenterDayOffset,
    getTimelineDayOffsetBounds,
    clampTimelineDayOffset,
} from 'components/Training/TrainingTimeline/Helpers/timelineConstants'

/**
 * The moving film strip inside the viewport.
 * Owns: scroll offset, translateX, wheel pan, snap, and animate-to-week.
 * Does not own: viewport width measurement (that's TrainingTimelineViewport).
 */
export default function TrainingTimelineTrack({
    viewportRef,
    viewportWidth = 0,
    dayWidth = 0,
    presentWeekOfTheYearKey: presentWeekOfTheYearKeyProp,
    timelineStartDate,
    timelineEndDate,
    timescale = 'week',
    onCenteredWeekIndexChange = () => {},
    animateTrackToWeekRef,
    children,
}) {
    const trackRef = useRef(null)
    const trackScrollOffsetInDaysRef = useRef(0)
    const centeredWeekIndexRef = useRef(0)
    const snapAnimationFrameRef = useRef(null)
    const snapTimeoutRef = useRef(null)

    const [centeredWeekIndex, setCenteredWeekIndex] = useState(0)

    const presentWeekOfTheYearKey = presentWeekOfTheYearKeyProp ?? WeekOfTheYearKey.constructWithWeekOffset(0).toString()

    const presentMonday = useMemo(
        () => new WeekOfTheYearKey(presentWeekOfTheYearKey).toDate().startOf('day'),
        [presentWeekOfTheYearKey],
    )

    const trackScrollDayOffsetBounds = useMemo(
        () => getTimelineDayOffsetBounds(presentMonday, timelineStartDate, timelineEndDate, timescale),
        [presentMonday, timelineStartDate, timelineEndDate, timescale],
    )

    const trackScrollDayOffsetBoundsRef = useRef(trackScrollDayOffsetBounds)
    trackScrollDayOffsetBoundsRef.current = trackScrollDayOffsetBounds

    const CancelSnapAnimation = useCallback(() => {
        if (snapAnimationFrameRef.current) {
            cancelAnimationFrame(snapAnimationFrameRef.current)
            snapAnimationFrameRef.current = null
        }

        if (snapTimeoutRef.current) {
            clearTimeout(snapTimeoutRef.current)
            snapTimeoutRef.current = null
        }
    }, [])

    const ApplyTrackTransform = useCallback(() => {
        const trackElement = trackRef.current

        if (!trackElement || viewportWidth <= 0 || dayWidth <= 0) {
            return
        }

        const viewportCenterX = viewportWidth / 2
        const centeredDayOffset = getViewportCenterDayOffset(trackScrollOffsetInDaysRef.current, timescale)
        trackElement.style.transform = `translateX(${viewportCenterX - centeredDayOffset * dayWidth}px)`
    }, [viewportWidth, dayWidth, timescale])

    const SyncCenteredWeekIndex = useCallback(() => {
        const nextCenteredWeekIndex = Math.round(trackScrollOffsetInDaysRef.current / DAYS_PER_WEEK)

        if (nextCenteredWeekIndex === centeredWeekIndexRef.current) {
            return
        }

        centeredWeekIndexRef.current = nextCenteredWeekIndex
        setCenteredWeekIndex(nextCenteredWeekIndex)
    }, [])

    const ClampTrackScrollOffsetInDays = useCallback((dayOffset) => {
        return clampTimelineDayOffset(dayOffset, trackScrollDayOffsetBoundsRef.current)
    }, [])

    const AnimateTrackToDayOffset = useCallback((rawTargetDayOffset) => {
        CancelSnapAnimation()

        const targetDayOffset = ClampTrackScrollOffsetInDays(rawTargetDayOffset)
        const startDayOffset = trackScrollOffsetInDaysRef.current
        const distanceInDays = targetDayOffset - startDayOffset

        if (Math.abs(distanceInDays) < 0.0001) {
            trackScrollOffsetInDaysRef.current = targetDayOffset
            ApplyTrackTransform()
            SyncCenteredWeekIndex()
            return
        }

        const animationStartTime = performance.now()

        const StepAnimation = (now) => {
            const progress = Math.min(1, (now - animationStartTime) / SNAP_DURATION_MS)
            trackScrollOffsetInDaysRef.current = startDayOffset + distanceInDays * easeOutCubic(progress)
            ApplyTrackTransform()
            SyncCenteredWeekIndex()

            if (progress < 1) {
                snapAnimationFrameRef.current = requestAnimationFrame(StepAnimation)
                return
            }

            trackScrollOffsetInDaysRef.current = targetDayOffset
            ApplyTrackTransform()
            SyncCenteredWeekIndex()
            snapAnimationFrameRef.current = null
        }

        snapAnimationFrameRef.current = requestAnimationFrame(StepAnimation)
    }, [CancelSnapAnimation, ClampTrackScrollOffsetInDays, ApplyTrackTransform, SyncCenteredWeekIndex])

    const AnimateTrackToWeek = useCallback((weekIndex) => {
        AnimateTrackToDayOffset(weekIndex * DAYS_PER_WEEK)
    }, [AnimateTrackToDayOffset])

    const ScheduleSnapToNearestWeek = useCallback(() => {
        if (snapTimeoutRef.current) {
            clearTimeout(snapTimeoutRef.current)
        }

        snapTimeoutRef.current = setTimeout(() => {
            const snapTargetDayOffset = getSnapTargetDay(trackScrollOffsetInDaysRef.current, timescale)
            AnimateTrackToDayOffset(ClampTrackScrollOffsetInDays(snapTargetDayOffset))
        }, SNAP_DELAY_MS)
    }, [AnimateTrackToDayOffset, ClampTrackScrollOffsetInDays, timescale])

    const HandleViewportWheelPan = useCallback((event) => {
        // Always claim the wheel event, even before the track is measured: an unclaimed horizontal swipe goes to the browser, which
        // navigates back / forward on macOS trackpads
        event.preventDefault()

        if (dayWidth <= 0) {
            return
        }

        CancelSnapAnimation()

        const pixelDelta = Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : event.deltaY
        const dayDelta = pixelDelta / dayWidth

        trackScrollOffsetInDaysRef.current = ClampTrackScrollOffsetInDays(
            trackScrollOffsetInDaysRef.current + dayDelta,
        )

        ApplyTrackTransform()
        SyncCenteredWeekIndex()
        ScheduleSnapToNearestWeek()
    }, [
        dayWidth,
        CancelSnapAnimation,
        ClampTrackScrollOffsetInDays,
        ApplyTrackTransform,
        SyncCenteredWeekIndex,
        ScheduleSnapToNearestWeek,
    ])

    // Re-clamp if the allowed date range changes.
    useEffect(() => {
        const clampedDayOffset = ClampTrackScrollOffsetInDays(trackScrollOffsetInDaysRef.current)

        if (clampedDayOffset === trackScrollOffsetInDaysRef.current) {
            return
        }

        trackScrollOffsetInDaysRef.current = clampedDayOffset
        ApplyTrackTransform()
        SyncCenteredWeekIndex()
    }, [trackScrollDayOffsetBounds, ClampTrackScrollOffsetInDays, ApplyTrackTransform, SyncCenteredWeekIndex])

    // Keep the strip centered when viewport size or day width changes.
    useEffect(() => {
        ApplyTrackTransform()
    }, [ApplyTrackTransform])

    // Wheel events fire on the viewport window; Track owns the pan behavior.
    useEffect(() => {
        const viewportElement = viewportRef?.current

        if (!viewportElement) {
            return undefined
        }

        viewportElement.addEventListener('wheel', HandleViewportWheelPan, { passive: false })

        return () => {
            viewportElement.removeEventListener('wheel', HandleViewportWheelPan)
        }
    }, [viewportRef, HandleViewportWheelPan])

    // Publish animate helper to the parent (selection clicks need it).
    useEffect(() => {
        if (!animateTrackToWeekRef) {
            return undefined
        }

        animateTrackToWeekRef.current = AnimateTrackToWeek

        return () => {
            animateTrackToWeekRef.current = () => {}
        }
    }, [animateTrackToWeekRef, AnimateTrackToWeek])

    // Tell parent which week is centered (drives render range).
    useEffect(() => {
        onCenteredWeekIndexChange(centeredWeekIndex)
    }, [centeredWeekIndex, onCenteredWeekIndexChange])

    useEffect(() => () => CancelSnapAnimation(), [CancelSnapAnimation])

    return (
        <div
            className="relative flex flex-col will-change-transform"
            ref={trackRef}
        >
            {children}
        </div>
    )
}
